import React from "react";
import {
  Wrench, BarChart3, BookOpen, ArrowLeftRight, Clock, Languages,
  Workflow, FileText, MoonStar, Globe2, Activity,
} from "lucide-react";
import { Reveal, SectionHeading } from "../Reveal";
import { capabilities } from "../../mock";

const iconMap = { Wrench, BarChart3, BookOpen, ArrowLeftRight, Clock, Languages };

// Small CSS-crafted visual per capability card
const CardVisual = ({ variant }) => {
  switch (variant) {
    case "tools":
      return (
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-400/30 bg-cyan-400/10"><Workflow className="h-4 w-4 text-cyan-300" /></div>
          <div className="h-px w-6 bg-gradient-to-r from-cyan-400/60 to-transparent" />
          <div className="flex flex-col gap-1.5">
            {["HIS", "CRM", "API"].map((t) => (
              <span key={t} className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-slate-300">{t}</span>
            ))}
          </div>
        </div>
      );
    case "insights":
      return (
        <div className="flex h-12 items-end gap-1.5">
          {[40, 65, 50, 85, 60, 100, 75].map((h, i) => (
            <span key={i} className="grow-bar w-3 rounded-t-sm bg-gradient-to-t from-cyan-500/40 to-cyan-300" style={{ height: `${h}%`, animationDelay: `${i * 0.12}s` }} />
          ))}
        </div>
      );
    case "knowledge":
      return (
        <div className="relative flex items-center">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-11 w-9 items-center justify-center rounded-md border border-white/10 bg-white/5" style={{ marginLeft: i ? "-14px" : 0, transform: `rotate(${(i - 1) * 6}deg)` }}>
              <FileText className="h-4 w-4 text-cyan-300/70" />
            </div>
          ))}
          <Activity className="ml-4 h-4 w-4 text-emerald-300" />
          <span className="ml-1.5 text-[10px] font-medium text-emerald-300">Learning</span>
        </div>
      );
    case "handoff":
      return (
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-medium text-cyan-200">AI Agent</span>
          <ArrowLeftRight className="h-4 w-4 text-slate-500" />
          <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-slate-200">PABX / Human</span>
        </div>
      );
    case "always":
      return (
        <div className="flex items-center gap-3">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/30">
            <MoonStar className="h-5 w-5 text-cyan-300" />
            <span className="blink-dot absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="font-display text-sm font-semibold text-slate-200">24 / 7 / 365</span>
        </div>
      );
    case "multilingual":
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {["AR", "EN", "HI", "UR", "TA", "FR"].map((l) => (
            <span key={l} className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-200">{l}</span>
          ))}
          <span className="flex items-center gap-1 text-[10px] font-medium text-cyan-300"><Globe2 className="h-3 w-3" /> +49</span>
        </div>
      );
    default:
      return null;
  }
};

const Capabilities = () => (
  <section id="capabilities" className="relative py-24 lg:py-32">
    <div className="bg-radial-glow absolute inset-0 opacity-60" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="AI Workforce"
        title={<>Advanced capabilities for your <span className="text-cyan-400">AI workforce</span></>}
        sub="Go beyond standard calls. Arkision agents come equipped with powerful tools to automate complex hospital workflows."
      />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {capabilities.map((c, i) => {
          const Icon = iconMap[c.icon];
          return (
            <Reveal key={c.title} delay={(i % 3) * 120}>
              <div className="card-dark card-dark-hover group flex h-full flex-col rounded-3xl p-7">
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 transition-colors duration-300 group-hover:bg-cyan-400/20">
                    <Icon className="h-5 w-5 text-cyan-300" />
                  </div>
                  <CardVisual variant={c.visual} />
                </div>
                <h3 className="font-display mt-6 text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{c.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default Capabilities;
